import { useMemo } from 'react'
import useFlightDeal from './useFlightDeal'

interface FeaturedDateRange {
  start: Date
  end: Date
}

interface UseFeaturedFlightDealsParams {
  flightDeals?: Array<App.FlightDeal>
  departureAirport?: App.AirportLocation
  arrivalAirport?: App.AirportLocation
  fareType: string
  showSegmentActiveDeals?: boolean
  flightDealId?: string
}

interface UseFeaturedFlightDealsResult {
  matchingSegmentDeals: Array<App.FlightDeal>
  featuredRanges: Array<FeaturedDateRange>
  displayDeal: App.FlightDeal | undefined
}

function useFeaturedFlightDeals(params: UseFeaturedFlightDealsParams): UseFeaturedFlightDealsResult {
  const {
    flightDeals,
    departureAirport,
    arrivalAirport,
    fareType,
    showSegmentActiveDeals,
    flightDealId,
  } = params

  const [flightDeal] = useFlightDeal(flightDealId)

  const matchingSegmentDeals = useMemo(() => {
    if (showSegmentActiveDeals && flightDeals && departureAirport && arrivalAirport) {
      return flightDeals.filter(deal => {
        return deal.originAirportCode === departureAirport.airportCode &&
          deal.destinationAirportCode === arrivalAirport.airportCode &&
          deal.fareType === fareType
      })
    }
    return []
  }, [flightDeals, showSegmentActiveDeals, departureAirport, arrivalAirport, fareType])

  const featuredRanges = useMemo(() => {
    const currentDealPeriods: Array<FeaturedDateRange> = flightDeal?.travelPeriods.map(period => ({
      start: new Date(period.departureDate),
      end: new Date(period.arrivalDate),
    })) || []

    matchingSegmentDeals.forEach(deal => {
      const periods = deal.travelPeriods.map(period => ({
        start: new Date(period.departureDate),
        end: new Date(period.arrivalDate),
      }))

      currentDealPeriods.push(...periods)
    })

    return currentDealPeriods
  }, [flightDeal, matchingSegmentDeals])

  // matchingSegmentDeals: used on landing page, flightDeal: used on deals page
  const displayDeal = useMemo(() => flightDeal ?? matchingSegmentDeals[0], [flightDeal, matchingSegmentDeals])

  return {
    matchingSegmentDeals,
    featuredRanges,
    displayDeal,
  }
}

export default useFeaturedFlightDeals
